import { isValidEmail } from '@bc-library/common'
import { nextTick, ref } from 'vue'
import { useApplicationStore } from '@/config/store/applicationStore'
import { evaluationApi } from '@/services/api'
import router from '@/config/router'

const getCreateEvaluationBasePayload = (newEvaluations) => {
  const { resources: _resources, campaign, ...basePayload } = newEvaluations

  return {
    ...basePayload,
    campaignId: campaign ? campaign._id : basePayload.campaignId,
    evaluationAverageId: basePayload.evaluationAverageId?._id || basePayload.evaluationAverageId || null,
    formularId: basePayload.formularId?._id || basePayload.formularId || null,
    formularIdAfter: basePayload.formularIdAfter?._id || basePayload.formularIdAfter || null,
  }
}



export const createEvaliation = async (newEvaluations) => {

  const applicationStore = useApplicationStore()

  console.log('[createEvaliation] Started with evaluations input:', newEvaluations)

  const evaluationsData = newEvaluations && 'value' in newEvaluations ? newEvaluations.value : newEvaluations

  evaluationsData.startDate.setHours(0, 0, 0, 0)
  evaluationsData.endDate.setHours(23, 59, 59, 999)
  if(evaluationsData.campaign)
    evaluationsData.campaignId = evaluationsData.campaign._id

  const baseEvaluationPayload = getCreateEvaluationBasePayload(evaluationsData)

  if (evaluationsData.isStereo) {
    evaluationsData.draftAccess = false
    evaluationsData.evaluatedCanNotify = false
    evaluationsData.showSkillMatrix = false
    evaluationsData.showSkillMatrixBefore = false
    evaluationsData.isBeforeAfter = true
    evaluationsData.signature = true
    evaluationsData.isExternal = false
  }

  if (evaluationsData.isExternal)
    evaluationsData.assesseeCanChoseAssessor = false

  if (evaluationsData.resources.length === 0) {
    applicationStore.pushError({
      title: 'Evaluation configuration error',
      translatedTitle: 'modules.evaluation.errors.noResourcesTitle',
      message: 'You must add at least one evaluated',
      translatedMessage: 'modules.evaluation.errors.noResources',
      color: 'error',
    })
    console.error('[createEvaliation] Error: No resources selected')
    throw new Error('You must add at least one evaluated')
  }

  if (!evaluationsData.formularId) {
    applicationStore.pushError({
      title: 'Evaluation configuration error',
      message: 'You must select a formular',
      color: 'error',
    })
    console.error('[createEvaliation] Error: No formular selected')
    throw new Error('You must select a formular')
  }
  if ((evaluationsData.isStereo || evaluationsData.isBeforeAfter) && !evaluationsData.formularIdAfter) {
    applicationStore.pushError({
      title: 'Evaluation configuration error',
      message: evaluationsData.isStereo
        ? 'You must select an assessor formular for stereo evaluation'
        : 'You must select a formular for after evaluation since you selected before/after evaluation',
      color: 'error',
    })
    console.error('[createEvaliation] Error: Stereo or Before/After chosen but no formularIdAfter')
    throw new Error('You must select a formular for after evaluation')
  }

  if (evaluationsData.isStereo && evaluationsData.resources.some(resource => resource.isExternal)) {
    applicationStore.pushError({
      title: 'Evaluation configuration error',
      message: 'External evaluators are not compatible with stereo evaluation',
      color: 'error',
    })
    console.error('[createEvaliation] Error: External evaluator with stereo')
    throw new Error('External evaluators are not compatible with stereo evaluation')
  }

  const evaluations = evaluationsData.resources.reduce((evaluations, resource) => {
    const evaluationsToAdd = []
    if (!resource.isExternal && evaluationsData.evaluationType === 0) {
      if (resource.evaluators.length === 0) {
        evaluationsToAdd.push({
          ...baseEvaluationPayload,
          startDate:evaluationsData.startDate,
          endDate:evaluationsData.endDate,
          managedBy: [],
        })
      }
      resource.evaluators.forEach((evaluator) => {
        evaluationsToAdd.push({
          ...baseEvaluationPayload,
          startDate:evaluationsData.startDate,
          endDate:evaluationsData.endDate,
          managedBy: [evaluator._id],
        })
      })
    }
    else {
      const isExternalEmailValide = isValidEmail(resource.externalMail)
      if (resource.isExternal) {
        if (!isExternalEmailValide) {
          applicationStore.pushError({
            title: `${resource.firstname} ${resource.lastname} evaluation configuration error`,
            message: 'External email is not valid',
            color: 'error',
          })
          console.error('[createEvaliation] Error: External email not valid for', resource)
          throw new Error('External email is not valid')
        }
        evaluationsToAdd.push({
          ...baseEvaluationPayload,
          startDate:evaluationsData.startDate,
          endDate:evaluationsData.endDate,
          managedBy: resource.evaluators.map(evaluator => evaluator._id),
          externalMail: resource.externalMail,
          isExternal: resource.externalMail.length > 0,
        })
      }
      else {
        evaluationsToAdd.push({
          ...baseEvaluationPayload,
          startDate:evaluationsData.startDate,
          endDate:evaluationsData.endDate,
          managedBy: resource.evaluators.map(evaluator => evaluator._id),
        })
      }
    }

    const trainingExecutionDate = new Date(evaluationsData.endDate)
    trainingExecutionDate.setDate(trainingExecutionDate.getDate() + 1)

    const updatedEvaluations = evaluationsToAdd.map((evaluation) => {
      return {
        ...evaluation,
        resourceId: resource._id,
        trainingExecutionDate: evaluation.isBeforeAfter ? evaluation.startDate : trainingExecutionDate,
      }
    })
    return [...evaluations, ...updatedEvaluations]
  }, [])

  console.log('[createEvaliation] Final evaluations payload to be sent to API:', evaluations)

  try {
    const response = await evaluationApi.createEvaluations(evaluations).promise
    console.log('[createEvaliation] API response success:', response)
    
    applicationStore.pushError({
      title: 'Evaluations created!',
      message: '',
      color: 'success',
    })
    
    console.log('[createEvaliation] Attempting redirection to name: Evaluations')
    await router.push({ name: 'Evaluations' })
    console.log('[createEvaliation] Redirection method executed')
  } catch (error) {
    console.error('[createEvaliation] API error caught during creation:', error)
    applicationStore.pushError({
      title: 'Error creating evaluations',
      message: error?.message || 'Unknown API error',
      color: 'error',
    })
  }
}

/**
 * usualy give the userStore resource
 * @param resource 
 * @returns 
 */
export const defaultEvaluationProjectCreation = (resource) => {
  return {
    isPeriod: false,
    display: '',
    is360: false,
    officeId: resource.currentJob.officeId,
    countryId: resource.currentJob.office.countryId,
    authorId: resource._id,
    _firmId: resource._firmId,
    isStereo: false,
    resources: [],
    projectId: undefined,
    evaluationForm: null,
    managedBy: [],
    evaluationType: 0,
    isExternal: false,
    signature: false,
    authorOnly: null,
    draftAccess: false,
    evaluatedCanNotify: false,
    isBeforeAfter: false,
    showSkillMatrix: false,
    hideProject: false,
    doNotSendMail: false,
    commentIsDisable: false,
    disableAbandon: false,
    evaluationAverageId: undefined,
    formularId: null,
    formularIdAfter: null,
    evaluations: [],
    assesseeCanChoseAssessor: false,
  
  }
}

export const useInfinityEvaluation = ({
  search = ref(''),
  limit = ref(20),
  page = ref(0),
  isLoading = ref(false),
  list = ref([]),
} = {}) => {
  const haveMore = ref(true)

  const getMoreData = async () => {
    try {
      isLoading.value = true
      const searchResponse = await evaluationApi.showEvaluationList({
        search: search.value,
        limit: limit.value,
        page: page.value,
      }).promise
      haveMore.value = !!searchResponse.count?.hasNextPage
      await nextTick()
      const docs = (searchResponse.docs || []).map((evaluation: any) => ({
        ...evaluation,
        display: evaluation.display
          || evaluation.name
          || evaluation.resource?.display
          || `${evaluation.resource?.firstname || ''} ${evaluation.resource?.lastname || ''}`.trim()
          || 'Évaluation',
      }))
      list.value = page.value === 0 ? docs : [...list.value, ...docs]
      return searchResponse
    }
    catch (err) {
      console.log('err', err)
    }
    finally {
      isLoading.value = false
    }
  }

  return {
    search,
    limit,
    page,
    haveMore,
    getMoreData,
    list,
    isLoading,
  }
}
